import { Link, router } from "@inertiajs/react"
import { AlertTriangle, RefreshCw, Cable } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { DropboxIntegrationState, SyncStatus } from "./types"

export function ClipsSyncStatusBanner({
  dropbox,
  sync,
}: {
  dropbox: DropboxIntegrationState
  sync: SyncStatus
}) {
  if (!dropbox.connected) {
    return (
      <div className="flex items-start justify-between gap-4 rounded-lg border border-hairline bg-surface p-4">
        <div className="flex gap-3">
          <Cable className="mt-0.5 h-5 w-5 text-accent" />
          <div>
            <p className="font-medium text-ink-display">Connect Dropbox to sync clips</p>
            <p className="text-sm text-ink-muted">
              Files from your B-roll root will appear here once you connect your Dropbox account.
            </p>
          </div>
        </div>
        <Button asChild>
          {/* Plain <a>, not Inertia <Link> — Inertia can't follow a 302 to dropbox.com. */}
          <a href="/auth/dropbox" className="no-underline">Connect Dropbox</a>
        </Button>
      </div>
    )
  }

  if (!dropbox.configured) {
    return (
      <div className="flex items-start justify-between gap-4 rounded-lg border border-signal-faded bg-signal-faded/30 p-4">
        <div className="flex gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-signal-display" />
          <div>
            <p className="font-medium text-ink-display">Pick a B-roll root folder</p>
            <p className="text-sm text-ink-muted">
              Dropbox is connected, but you haven&apos;t picked a folder yet. Sync won&apos;t run until you do.
            </p>
          </div>
        </div>
        <Button variant="secondary" asChild>
          <Link href="/settings?tab=dropbox" className="no-underline">Open settings</Link>
        </Button>
      </div>
    )
  }

  if (sync && sync.outcome === "errored") {
    return (
      <div className="flex items-start justify-between gap-4 rounded-lg border border-destructive-faded bg-destructive-faded/30 p-4">
        <div className="flex gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-destructive" />
          <div>
            <p className="font-medium text-ink-display">Last Dropbox sync failed</p>
            <p className="text-sm text-ink-muted">
              {Array.isArray(sync.details?.errors) && sync.details?.errors[0]
                ? (sync.details?.errors as Array<{ message?: string }>)[0]?.message
                : "Check the AgentRun details for more."}
            </p>
          </div>
        </div>
        <Button
          variant="secondary"
          onClick={() => router.post("/clips/trigger_sync", {}, { preserveScroll: true })}
        >
          <RefreshCw className="mr-1 h-4 w-4" />
          Try again
        </Button>
      </div>
    )
  }

  return null
}
